"use client";

import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Map } from "@/lib/types/Map";
import { createEmptyMap } from "@/lib/utils/createEmptyMap";
import useGameFilterMaps from "@/lib/utils/useGameFilterMaps";
import { useGame } from "./GameContextProvider";
import { useMainMap } from "./MainMapContextProvider";
import { MapType } from "@/lib/types/common/MapType";
import { Expansion } from "@/lib/types/common/Expansion";

interface GameMapContextType {
    availableMaps: Map[];
    currentMap: Map;
    isLoading: boolean;
    error: string | null;
    setCurrentMapById: (mapId: string | null) => void;
    refreshMaps: () => Promise<void>;
    findCorrectWorldMapVersion?: (targetMapId: string) => string;
}

const GameMapContext = createContext<GameMapContextType>({
    availableMaps: [],
    currentMap: {} as Map,
    isLoading: false,
    error: null,
    setCurrentMapById: () => { },
    refreshMaps: async () => { },
    findCorrectWorldMapVersion: () => "",
});

export function GameMapProvider({ children }: { children: ReactNode }) {
    const mainMapContext = useMainMap();
    const { gameState } = useGame();
    const [currentMap, setCurrentMap] = useState<Map>(createEmptyMap());

    // Get filtered maps based on game state selections
    const selectedExpansions = gameState?.selectedExpansions ? Array.from(gameState.selectedExpansions) : [];
    const selectedMapTypes = gameState?.selectedMapTypes ? Array.from(gameState.selectedMapTypes) : [];

    const availableMaps = useGameFilterMaps(mainMapContext.allMaps, selectedExpansions, selectedMapTypes);

    const setCurrentMapById = (mapId: string | null) => {
        const foundMap = availableMaps.find(map => map.id === mapId);
        if (foundMap) {
            setCurrentMap(foundMap);
        }
    };

    // Helper function to get the latest expansion from selected expansions
    const getLatestSelectedExpansion = (): Expansion | null => {
        if (!selectedExpansions || selectedExpansions.length === 0) return null;
        const expansionOrder = [Expansion.ARR, Expansion.HW, Expansion.SB, Expansion.ShB, Expansion.EW, Expansion.DT];
        const expansionsAsEnum = selectedExpansions as Expansion[];
        let latestExpansion: Expansion | null = null;
        for (const exp of expansionOrder) {
            if (expansionsAsEnum.includes(exp)) {
                latestExpansion = exp;
            }
        }
        return latestExpansion;
    };

    // Helper function to find the correct world map version based on latest expansion
    const findCorrectWorldMapVersion = (targetMapId: string): string => {
        const worldMaps = availableMaps.filter(m => m.type === MapType.WORLD_MAP);
        const targetMapName = availableMaps.find(m => m.id === targetMapId)?.name;

        if (targetMapName?.en === "The First") {
            const theFirstWorldMaps = worldMaps.filter(m => m.name.en === "The First");
            const latestExpansion = getLatestSelectedExpansion();
            const foundTheFirstMap = theFirstWorldMaps.find(m => m.expansion === latestExpansion);
            if (foundTheFirstMap) {
                return foundTheFirstMap.id;
            }
        } else {
            const hydaelynWorldMaps = worldMaps.filter(m => m.name.en !== "The First");
            const latestExpansion = getLatestSelectedExpansion();
            const foundHydaelynMap = hydaelynWorldMaps.find(m => m.expansion === latestExpansion);
            if (foundHydaelynMap) {
                return foundHydaelynMap.id;
            }
        };
        // Fallback to the original map if no suitable version found
        return targetMapId;
    }

    // Auto-select the first available map when maps change, but allow free navigation
    useEffect(() => {
        if (availableMaps.length > 0 && (!currentMap?.id || !availableMaps.find(m => m.id === currentMap.id))) {
            const worldMapId = findCorrectWorldMapVersion(availableMaps[0].id);
            setCurrentMapById(worldMapId);
        }
    }, [availableMaps, currentMap]);

    return (
        <GameMapContext.Provider
            value={{
                availableMaps,
                currentMap,
                isLoading: mainMapContext.isLoading,
                error: mainMapContext.error,
                setCurrentMapById,
                refreshMaps: mainMapContext.refreshMaps,
                findCorrectWorldMapVersion,
            }}
        >
            {children}
        </GameMapContext.Provider>
    );
}

export function useGameMap(): GameMapContextType {
    return useContext(GameMapContext);
}